import axios from 'axios';
import { getApiV1BaseUrl } from '../config/runtime';

const API_URL = getApiV1BaseUrl();

// Instância axios para autenticação (sem token por padrão)
const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Instância axios para requisições autenticadas (com token)
const authenticatedApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token automaticamente
authenticatedApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar erros de autenticação
authenticatedApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // LOGIN / LOGOUT
  login(username, password) {
    return authApi
      .post('/auth/login', { username, password })
      .then((res) => res.data);
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // GERENCIAMENTO DE USUÁRIOS (admin only)
  getUsers() {
    return authenticatedApi.get('/users').then((res) => res.data);
  },

  createUser(username, password, role = 'USER') {
    return authenticatedApi
      .post('/users', { username, password, role })
      .then((res) => res.data);
  },

  updateUser(userId, updates) {
    return authenticatedApi
      .put(`/users/${userId}`, updates)
      .then((res) => res.data);
  },

  deleteUser(userId) {
    return authenticatedApi.delete(`/users/${userId}`).then((res) => res.data);
  },

  resetUserPassword(userId, newPassword) {
    return authenticatedApi
      .post(`/users/${userId}/reset-password`, { newPassword })
      .then((res) => res.data);
  },

  // CREDENCIAIS DO USUÁRIO
  getCredentials() {
    return authenticatedApi
      .get('/user/credentials')
      .then((res) => res.data)
      .catch((err) => ({
        success: false,
        credentials: null,
        has_polymarket: false,
        has_gemini: false,
        has_coingecko: false,
        last_updated: null
      }));
  },

  setCredentials(credentials) {
    return authenticatedApi
      .post('/user/credentials', credentials)
      .then((res) => res.data);
  },

  deleteCredentials(serviceType) {
    return authenticatedApi
      .delete(`/user/credentials/${serviceType}`)
      .then((res) => res.data);
  },

  // CONFIGURAÇÕES DO USUÁRIO
  getConfig() {
    return authenticatedApi.get('/user/config').then((res) => res.data);
  },

  updateConfig(config) {
    return authenticatedApi
      .put('/user/config', config)
      .then((res) => res.data);
  },

  getRuntimeProfile() {
    return authenticatedApi.get('/runtime/profile').then((res) => res.data);
  },

  getBotStatus() {
    return authenticatedApi.get('/bot/status').then((res) => res.data);
  },

  getBotLogs(lines = 200) {
    return authenticatedApi.get(`/bot/logs?lines=${lines}`).then((res) => res.data);
  },

  startBot() {
    return authenticatedApi.post('/bot/start').then((res) => res.data);
  },

  stopBot() {
    return authenticatedApi.post('/bot/stop').then((res) => res.data);
  },

  restartBot() {
    return authenticatedApi.post('/bot/restart').then((res) => res.data);
  },

  // ESTATÍSTICAS DO USUÁRIO
  getUserStats() {
    return authenticatedApi
      .get('/user/stats')
      .then((res) => res.data)
      .catch((err) => ({
        total_trades: 0,
        won_trades: 0,
        lost_trades: 0,
        win_rate: 0,
        total_profit: 0,
        roi: 0
      }));
  },

  // HISTÓRICO DO USUÁRIO
  getUserTrades(limit = 50) {
    return authenticatedApi
      .get(`/user/trades?limit=${limit}`)
      .then((res) => res.data);
  },

  getUserBankHistory(limit = 50) {
    return authenticatedApi
      .get(`/user/bank-history?limit=${limit}`)
      .then((res) => res.data);
  },

  // AUDIT LOG
  getAuditLog(limit = 100) {
    return authenticatedApi
      .get(`/audit?limit=${limit}`)
      .then((res) => res.data);
  },

  // AGENTES DE MERCADO
  getAgents() {
    return authenticatedApi.get('/user/agents').then((res) => res.data);
  },

  saveAgents(agents) {
    return authenticatedApi.post('/user/agents/bulk', { agents }).then((res) => res.data);
  },

  deleteAgent(agentId) {
    return authenticatedApi.delete(`/user/agents/${agentId}`).then((res) => res.data);
  },

  getMarketPermissions() {
    return authenticatedApi.get('/user/market-permissions').then((res) => res.data);
  },

  // ADMIN — agentes e permissões de outros usuários
  getAdminDashboard() {
    return authenticatedApi.get('/admin/dashboard').then((res) => res.data);
  },

  getAdminUserAgents(userId) {
    return authenticatedApi.get(`/admin/users/${userId}/agents`).then((res) => res.data);
  },

  saveAdminUserAgents(userId, agents) {
    return authenticatedApi.post(`/admin/users/${userId}/agents/bulk`, { agents }).then((res) => res.data);
  },

  getAdminUserPermissions(userId) {
    return authenticatedApi.get(`/admin/users/${userId}/market-permissions`).then((res) => res.data);
  },

  setAdminUserPermissions(userId, categories) {
    return authenticatedApi.put(`/admin/users/${userId}/market-permissions`, { categories }).then((res) => res.data);
  },

  getAdminUserLimits(userId) {
    return authenticatedApi.get(`/admin/users/${userId}/limits`).then((res) => res.data);
  },

  setAdminUserLimits(userId, max_agents) {
    return authenticatedApi.put(`/admin/users/${userId}/limits`, { max_agents }).then((res) => res.data);
  },

  getMarketCategories() {
    return authenticatedApi.get('/admin/market-categories').then((res) => res.data);
  },

  // UTILITY
  setToken(token) {
    if (token) {
      authenticatedApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    }
  },

  clearToken() {
    delete authenticatedApi.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  },

  getToken() {
    return localStorage.getItem('token');
  }
};
